import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import { API_BASE } from '../config';
import { dispatchMentionableEntitiesChangedForDocument } from '../utils/mentionable-entities-events';
import { mapMentionableEntitiesFromApiResponse } from '../utils/mentionable-entities';
import { clampEntityRelevance } from '../utils/entity-relevance';
import {
  buildReferencedInByTargetId,
  type EntityReferenceEdge,
} from '../utils/related-entities';
import { CharacterDetail } from './CharacterDetail';
import { AnalyzeButton } from './AnalyzeButton';
import { AddItemButton } from './AddItemButton';
import { Button } from './ui/button';
import { PANEL_BODY_FILL_CLASSNAME } from '../constants/layout';
import { PanelPageHeader } from './PanelPageHeader';
import { HeaderCreateActions } from './HeaderPageActions';
import { CharactersEmptyState } from './CharactersEmptyState';
import { DisabledTooltip } from './EmptyStateSecondaryButton';
import { useDocumentWordCount } from '../hooks/useDocumentWordCount';
import {
  getDocumentAnalyzeTooShortTooltip,
  isDocumentTooShortToAnalyze,
} from '../utils/feedback-analyze-availability';
import { Dialog, DialogContent } from './ui/dialog';
import { ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME } from './EntityDetailModalLayout';
import {
  CharactersTable,
  type CharactersBulkPatch,
  type CharactersTableRow,
} from './CharactersTable';
import { getCharacterDetailHref } from '../utils/document-panel-paths';
import { openAppPathInNewTab } from '../utils/app-navigation';
import type { MentionableEntity } from './RichTextField';

interface Character {
  id: string;
  name: string;
  description?: string;
  role?: string;
  appearance?: string;
  personality?: string;
  background?: string;
  relationships?: string;
  notes?: string;
  inconsistencies?: string;
  improvements?: string;
  isManual?: boolean;
  userNotes?: string;
  relevance?: number;
}

interface CharactersProps {
  documentId?: string;
  projectId?: string;
  isSidebar?: boolean;
  onClose?: () => void;
  initialCharacterId?: string;
}

export const Characters: React.FC<CharactersProps> = ({ documentId: propDocumentId, projectId: propProjectId, isSidebar = false, onClose, initialCharacterId }) => {
  const params = useParams<{ projectId?: string; documentId: string }>();
  const routeDocumentId = params.documentId;
  const routeProjectId = params.projectId;
  const documentId = propDocumentId || routeDocumentId;
  const projectId = propProjectId || routeProjectId;
  const { token } = useAuth();
  const { t } = useLocale();
  const { showToast, showFieldSavedToast, showErrorToast } = useToast();
  const [masterKey] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('masterKey');
  });
  const [shareToken] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('shareToken');
  });
  const canEdit = !shareToken;
  const [characters, setCharacters] = useState<Character[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analyzingCharacterId, setAnalyzingCharacterId] = useState<string | null>(null);
  const [selectedCharacterId, setSelectedCharacterId] = useState<string | null>(null);
  const [detailScrollToSection, setDetailScrollToSection] = useState<
    'related_entities' | undefined
  >(undefined);
  const [analyzing, setAnalyzing] = useState(false);
  const [mentionableEntities, setMentionableEntities] = useState<MentionableEntity[]>([]);
  const [entityRefs, setEntityRefs] = useState<EntityReferenceEdge[]>([]);
  const documentWordCount = useDocumentWordCount(documentId, token, masterKey);
  const isAnalyzeDisabled = isDocumentTooShortToAnalyze(documentWordCount);
  const analyzeTooShortTooltip =
    documentWordCount !== null && isAnalyzeDisabled
      ? getDocumentAnalyzeTooShortTooltip(t)
      : undefined;
  const analyzeCharactersFromDocumentTooltip = t('analyze_characters_from_document_tooltip');
  const analyzeTooltip = analyzeTooShortTooltip ?? analyzeCharactersFromDocumentTooltip;

  useEffect(() => {
    if (!selectedCharacterId) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setSelectedCharacterId(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedCharacterId]);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchCharacters = async (): Promise<void> => {
      if ((!token && !masterKey) || !documentId) {
        setLoading(false);
        return;
      }

      const pollCharacters = async (): Promise<void> => {
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/characters?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/characters`;

        let timeoutId: NodeJS.Timeout | null = null;

        try {
          timeoutId = setTimeout(() => {
            if (!abortController.signal.aborted) {
              abortController.abort();
            }
          }, 60000);

          const response = await fetch(url, {
            headers: {
              ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
            },
            signal: abortController.signal,
          });

          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }

          if (response.status === 202) {
            const data = await response.json();
            if (data.status === 'processing') {
              setTimeout(() => {
                if (!abortController.signal.aborted) {
                  pollCharacters();
                }
              }, 2000);
              return;
            }
          }

          if (!response.ok) {
            throw new Error('Failed to fetch characters');
          }

          const data = await response.json();

          let charactersData: Character[] = [];

          if (Array.isArray(data)) {
            charactersData = data;
          } else if (data.characters) {
            charactersData = data.characters;
          } else if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
            const keys = Object.keys(data);
            if (keys.length > 0 && typeof data[keys[0]] === 'object') {
              charactersData = Object.entries(data).map(([name, char]: [string, any]) => {
                if (typeof char === 'object' && char !== null) {
                  return {
                    id: char.id || `temp-${Math.random().toString(36).substr(2, 9)}`,
                    name: name || char.name || '',
                    ...char,
                  };
                }
                return {
                  id: `temp-${Math.random().toString(36).substr(2, 9)}`,
                  name,
                  description: String(char),
                };
              });
            }
          }

          if (!Array.isArray(charactersData)) {
            console.error('Characters data is not an array after normalization:', charactersData);
            console.error('Original data:', data);
            charactersData = [];
          }

          charactersData = charactersData.map((char) => ({
            id: char.id || `temp-${Math.random().toString(36).substr(2, 9)}`,
            name: String(char.name || ''),
            description: char.description ? String(char.description) : undefined,
            role: char.role ? String(char.role) : undefined,
            appearance: char.appearance ? String(char.appearance) : undefined,
            personality: char.personality ? String(char.personality) : undefined,
            background: char.background ? String(char.background) : undefined,
            relationships: char.relationships ? String(char.relationships) : undefined,
            notes: char.notes ? String(char.notes) : undefined,
            inconsistencies: char.inconsistencies ? String(char.inconsistencies) : undefined,
            improvements: char.improvements ? String(char.improvements) : undefined,
            isManual: typeof char.isManual === 'boolean' ? char.isManual : undefined,
            userNotes: char.userNotes ? String(char.userNotes) : undefined,
            relevance: clampEntityRelevance(char.relevance),
          }));

          charactersData = charactersData.filter((char): char is Character =>
            typeof char === 'object' && char !== null && 'name' in char && 'id' in char
          );

          if (!Array.isArray(charactersData)) {
            console.error('FINAL CHECK: charactersData is not an array!', charactersData);
            charactersData = [];
          }

          setCharacters(charactersData);
          setLoading(false);
          setError(null);
        } catch (fetchError) {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          if (fetchError instanceof Error && fetchError.name === 'AbortError') {
            return;
          }
          throw fetchError;
        }
      };

      const pollWithRetry = async (retryCount = 0): Promise<void> => {
        try {
          await pollCharacters();
        } catch (err) {
          if (err instanceof Error && err.name === 'AbortError') {
            return;
          }

          if (retryCount < 3) {
            const delay = Math.min(2000 * Math.pow(2, retryCount), 10000);
            setTimeout(() => {
              if (!abortController.signal.aborted) {
                pollWithRetry(retryCount + 1);
              }
            }, delay);
            return;
          }

          console.error('Error fetching characters after retries:', err);
          setError('Error al cargar los personajes. Por favor, intenta de nuevo.');
          setLoading(false);
        }
      };

      try {
        setLoading(true);
        setError(null);
        await pollWithRetry();
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        console.error('Error fetching characters:', err);
        setError('Error al cargar los personajes');
        setLoading(false);
      }
    };

    fetchCharacters();

    return () => {
      abortController.abort();
    };
  }, [token, documentId, masterKey]);

  useEffect(() => {
    if (!initialCharacterId || loading || !characters.length) return;
    const exists = characters.some((c) => c.id === initialCharacterId);
    if (exists) {
      setSelectedCharacterId(initialCharacterId);
    }
  }, [initialCharacterId, loading, characters]);

  useEffect(() => {
    if ((!token && !masterKey) || !projectId) {
      setMentionableEntities([]);
      setEntityRefs([]);
      return;
    }

    const authHeaders: Record<string, string> =
      token && !masterKey ? { Authorization: `Bearer ${token}` } : {};
    let cancelled = false;

    const loadRelatedData = async (): Promise<void> => {
      try {
        const [mentionableResponse, refsResponse] = await Promise.all([
          fetch(`${API_BASE}/api/projects/${projectId}/mentionable-entities`, {
            headers: { ...authHeaders },
          }),
          fetch(`${API_BASE}/api/projects/${projectId}/entity-references`, {
            headers: { ...authHeaders },
          }),
        ]);
        if (cancelled) {
          return;
        }
        if (mentionableResponse.ok) {
          const data = await mentionableResponse.json();
          setMentionableEntities(mapMentionableEntitiesFromApiResponse(data));
        } else {
          setMentionableEntities([]);
        }
        if (refsResponse.ok) {
          const data = await refsResponse.json();
          setEntityRefs(Array.isArray(data.references) ? data.references : []);
        } else {
          setEntityRefs([]);
        }
      } catch {
        if (!cancelled) {
          setMentionableEntities([]);
          setEntityRefs([]);
        }
      }
    };

    void loadRelatedData();
    return () => {
      cancelled = true;
    };
  }, [token, masterKey, projectId]);

  const tableCharacters = useMemo<CharactersTableRow[]>(() => {
    if (!documentId) {
      return characters;
    }
    const referencedInById = buildReferencedInByTargetId(
      'character',
      documentId,
      entityRefs,
      mentionableEntities
    );
    return characters.map((character) => ({
      ...character,
      referencedIn: referencedInById.get(character.id) ?? [],
    }));
  }, [characters, documentId, entityRefs, mentionableEntities]);

  const handleAnalyze = useCallback(async (): Promise<void> => {
    if (analyzing || isAnalyzeDisabled) return;

    const abortController = new AbortController();

    const pollAnalyze = async (): Promise<void> => {
      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}/characters/analyze?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}/characters/analyze`;
      const authToken = shareToken || token;

      let timeoutId: NodeJS.Timeout | null = null;

      try {
        timeoutId = setTimeout(() => {
          if (!abortController.signal.aborted) {
            abortController.abort();
          }
        }, 60000);

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            ...(authToken && !masterKey ? { Authorization: `Bearer ${authToken}` } : {}),
          },
          signal: abortController.signal,
        });

        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }

        if (response.status === 202) {
          const data = await response.json();
          if (data.status === 'processing') {
            setTimeout(() => {
              if (!abortController.signal.aborted) {
                pollAnalyze();
              }
            }, 2000);
            return;
          }
        }

        if (!response.ok) {
          throw new Error('Failed to analyze characters');
        }

        const data = await response.json();

        let charactersData: Character[] = [];

        if (Array.isArray(data)) {
          charactersData = data;
        } else if (data.characters) {
          charactersData = data.characters;
        } else if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
          const keys = Object.keys(data);
          if (keys.length > 0 && typeof data[keys[0]] === 'object') {
            charactersData = Object.entries(data).map(([name, char]: [string, any]) => {
              if (typeof char === 'object' && char !== null) {
                return { name, ...char };
              }
              return { name, description: String(char) };
            });
          }
        }

        if (!Array.isArray(charactersData)) {
          charactersData = [];
        }

        charactersData = charactersData.map((char) => ({
          id: char.id || `temp-${Math.random().toString(36).substr(2, 9)}`,
          name: String(char.name || ''),
          description: char.description ? String(char.description) : undefined,
          role: char.role ? String(char.role) : undefined,
          appearance: char.appearance ? String(char.appearance) : undefined,
          personality: char.personality ? String(char.personality) : undefined,
          background: char.background ? String(char.background) : undefined,
          relationships: char.relationships ? String(char.relationships) : undefined,
          notes: char.notes ? String(char.notes) : undefined,
          inconsistencies: char.inconsistencies ? String(char.inconsistencies) : undefined,
          improvements: char.improvements ? String(char.improvements) : undefined,
          isManual: typeof char.isManual === 'boolean' ? char.isManual : undefined,
          userNotes: char.userNotes ? String(char.userNotes) : undefined,
          relevance: clampEntityRelevance(char.relevance),
        }));

        setCharacters(charactersData);
        setAnalyzing(false);
        setError(null);
      } catch (fetchError) {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (fetchError instanceof Error && fetchError.name === 'AbortError') {
          return;
        }
        throw fetchError;
      }
    };

    const pollAnalyzeWithRetry = async (retryCount = 0): Promise<void> => {
      try {
        await pollAnalyze();
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        if (retryCount < 3) {
          const delay = Math.min(2000 * Math.pow(2, retryCount), 10000);
          setTimeout(() => {
            if (!abortController.signal.aborted) {
              pollAnalyzeWithRetry(retryCount + 1);
            }
          }, delay);
          return;
        }

        console.error('Error analyzing characters after retries:', err);
        setError('Error al analizar los personajes. Por favor, intenta de nuevo.');
        setAnalyzing(false);
      }
    };

    try {
      setAnalyzing(true);
      setError(null);
      await pollAnalyzeWithRetry();
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      console.error('Error analyzing characters:', err);
      setError('Error al analizar los personajes');
      setAnalyzing(false);
    }
  }, [analyzing, isAnalyzeDisabled, masterKey, documentId, shareToken, token]);

  const handleAnalyzeCharacter = useCallback(
    async (character: CharactersTableRow): Promise<void> => {
      if (analyzingCharacterId === character.id) {
        return;
      }

      try {
        setAnalyzingCharacterId(character.id);
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/characters/${character.id}/analyze?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/characters/${character.id}/analyze`;
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to analyze character');
        }

        const data = await response.json();
        const updatedCharacter = data.character;

        setCharacters((prev) =>
          prev.map((char) =>
            char.id === character.id
              ? {
                  ...char,
                  inconsistencies: updatedCharacter.inconsistencies,
                  improvements: updatedCharacter.improvements,
                  relevance: clampEntityRelevance(updatedCharacter.relevance) ?? char.relevance,
                }
              : char
          )
        );
      } catch (err) {
        console.error('Error analyzing character:', err);
        setError(t('failed_to_analyze_character'));
      } finally {
        setAnalyzingCharacterId(null);
      }
    },
    [analyzingCharacterId, documentId, masterKey, t, token]
  );

  const handleCharacterRowClick = useCallback(
    (character: CharactersTableRow, event: React.MouseEvent): void => {
      if (!documentId) {
        return;
      }
      if (event.ctrlKey || event.metaKey) {
        openAppPathInNewTab(getCharacterDetailHref(projectId, documentId, character.id));
        return;
      }
      setDetailScrollToSection(undefined);
      setSelectedCharacterId(character.id);
    },
    [documentId, projectId]
  );

  const handleSeeAllReferencedIn = useCallback((character: CharactersTableRow): void => {
    setDetailScrollToSection('related_entities');
    setSelectedCharacterId(character.id);
  }, []);

  const handleBulkPatchCharacters = useCallback(
    async (characterIds: string[], patch: CharactersBulkPatch): Promise<void> => {
      if (!documentId || characterIds.length === 0) {
        return;
      }
      if (!token && !masterKey) {
        showErrorToast(t('characters_assign_failed'));
        return;
      }

      const previousCharacters = characters;
      const characterIdSet = new Set(characterIds);
      setCharacters((prev) =>
        prev.map((character) => {
          if (!characterIdSet.has(character.id)) {
            return character;
          }
          return {
            ...character,
            ...(patch.role !== undefined ? { role: patch.role } : {}),
            ...(patch.relevance !== undefined
              ? {
                  relevance:
                    patch.relevance === null
                      ? undefined
                      : clampEntityRelevance(patch.relevance),
                }
              : {}),
          };
        })
      );

      try {
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/characters/bulk?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/characters/bulk`;
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            characterIds,
            ...(patch.role !== undefined ? { role: patch.role } : {}),
            ...(patch.relevance !== undefined ? { relevance: patch.relevance } : {}),
          }),
        });
        if (!response.ok) {
          throw new Error('assign failed');
        }
        if (patch.role !== undefined) {
          showFieldSavedToast(t('character_role'));
        } else if (patch.relevance !== undefined) {
          showFieldSavedToast(t('entity_relevance'));
        }
      } catch (err) {
        console.error('Error bulk updating characters:', err);
        setCharacters(previousCharacters);
        showErrorToast(t('characters_assign_failed'));
      }
    },
    [
      characters,
      documentId,
      masterKey,
      showErrorToast,
      showFieldSavedToast,
      t,
      token,
    ]
  );

  const handleBulkDeleteCharacters = useCallback(
    async (characterIds: string[]): Promise<void> => {
      if (!documentId || characterIds.length === 0) {
        return;
      }
      if (!token && !masterKey) {
        showErrorToast(t('failed_to_delete_character'));
        return;
      }

      const results = await Promise.allSettled(
        characterIds.map(async (characterId) => {
          const url = masterKey
            ? `${API_BASE}/api/documents/${documentId}/characters/${characterId}?masterKey=${encodeURIComponent(masterKey)}`
            : `${API_BASE}/api/documents/${documentId}/characters/${characterId}`;
          const response = await fetch(url, {
            method: 'DELETE',
            headers: {
              ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
            },
          });
          if (!response.ok) {
            throw new Error('delete failed');
          }
          return characterId;
        })
      );

      const deletedIds = results
        .filter((result): result is PromiseFulfilledResult<string> => result.status === 'fulfilled')
        .map((result) => result.value);
      const failed = results.length - deletedIds.length;

      if (deletedIds.length > 0) {
        const deletedSet = new Set(deletedIds);
        setCharacters((prev) => prev.filter((character) => !deletedSet.has(character.id)));
        setSelectedCharacterId((current) =>
          current && deletedSet.has(current) ? null : current
        );
        void dispatchMentionableEntitiesChangedForDocument(
          documentId,
          token ?? null,
          masterKey,
          projectId
        );
        showToast(
          deletedIds.length === 1
            ? t('character_deleted_success')
            : t('characters_deleted_success').replace('{count}', String(deletedIds.length))
        );
      }
      if (failed > 0) {
        showErrorToast(
          t('characters_delete_partial_failed').replace('{count}', String(failed))
        );
      }
    },
    [documentId, masterKey, projectId, showErrorToast, showToast, t, token]
  );

  if (loading) {
    if (isSidebar) {
      return (
        <div style={{ padding: '1rem' }}>
          <div style={{ textAlign: 'center' }}>{t('loading')}</div>
        </div>
      );
    }
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>{t('loading')}</div>
    );
  }

  if (error) {
    if (isSidebar) {
      return (
        <div style={{ padding: '1rem' }}>
          <div style={{ color: '#ef4444', textAlign: 'center' }}>{error}</div>
        </div>
      );
    }
    return (
      <div>
        <PanelPageHeader
          onClose={onClose}
          closeAriaLabel={t('close')}
        />
        <div style={{ color: '#ef4444', textAlign: 'center', padding: '3rem' }}>{error}</div>
      </div>
    );
  }

  let charactersContent: React.ReactNode;

  if (!Array.isArray(characters)) {
    console.error('RENDER: characters is not an array!', characters);
    charactersContent = (
      <div style={{ textAlign: 'center', padding: '3rem', color: '#ef4444' }}>
        <p style={{ fontSize: '1.125rem' }}>Error: Formato de datos inválido</p>
        <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
          Por favor, recarga la página
        </p>
      </div>
    );
  } else if (characters.length === 0) {
    charactersContent = (
      <CharactersEmptyState
        canEdit={canEdit}
        onAdd={() => {
          setDetailScrollToSection(undefined);
          setSelectedCharacterId('new');
        }}
        onAnalyze={() => {
          void handleAnalyze();
        }}
        isAnalyzing={analyzing}
        analyzeDisabled={isAnalyzeDisabled}
        analyzeTooltip={analyzeTooltip}
      />
    );
  } else {
    charactersContent = (
      <CharactersTable
        characters={tableCharacters}
        analyzingCharacterId={analyzingCharacterId}
        onRowClick={handleCharacterRowClick}
        onAnalyzeCharacter={(character) => {
          void handleAnalyzeCharacter(character);
        }}
        analyzeTooltip={t('analyze_character_tooltip')}
        canEdit={canEdit}
        onBulkPatch={canEdit ? handleBulkPatchCharacters : undefined}
        onBulkDelete={canEdit ? handleBulkDeleteCharacters : undefined}
        projectId={projectId}
        onSeeAllReferencedIn={handleSeeAllReferencedIn}
      />
    );
  }

  const addCharacterButton = (
    <AddItemButton
      onClick={() => {
        setDetailScrollToSection(undefined);
        setSelectedCharacterId('new');
      }}
      label={t('add_character') || 'Add character'}
    />
  );
  const analyzeCharactersButton = (
    <DisabledTooltip enabled={isAnalyzeDisabled} text={analyzeTooShortTooltip}>
      <AnalyzeButton
        className="mr-0"
        onClick={() => {
          void handleAnalyze();
        }}
        isAnalyzing={analyzing}
        disabled={isAnalyzeDisabled}
        tooltip={isAnalyzeDisabled ? undefined : analyzeCharactersFromDocumentTooltip}
      >
        {t('analyze_characters_from_document')}
      </AnalyzeButton>
    </DisabledTooltip>
  );

  const content = (
    <div className={`${PANEL_BODY_FILL_CLASSNAME} overflow-auto`} style={isSidebar ? { padding: '1rem' } : {}}>
      {!isSidebar ? (
        <HeaderCreateActions hasItems={characters.length > 0}>
          {analyzeCharactersButton}
          {addCharacterButton}
        </HeaderCreateActions>
      ) : null}
      <PanelPageHeader
        onClose={!isSidebar ? onClose : undefined}
        closeAriaLabel={t('close')}
        endActions={
          isSidebar && characters.length > 0 ? (
            <>
              {analyzeCharactersButton}
              {addCharacterButton}
            </>
          ) : undefined
        }
      />
      {charactersContent}
    </div>
  );

  const characterDetailModal = (
    <Dialog
      open={selectedCharacterId !== null}
      onOpenChange={(open) => {
        if (!open) {
          setSelectedCharacterId(null);
          setDetailScrollToSection(undefined);
        }
      }}
    >
      {selectedCharacterId !== null && (
        <DialogContent
          className={`${ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME}${
            selectedCharacterId === 'new' ? '' : ' h-[90vh]'
          }`}
        >
            <CharacterDetail
              documentId={documentId}
              projectId={projectId}
              characterId={selectedCharacterId === 'new' ? null : selectedCharacterId}
              onClose={() => {
                setSelectedCharacterId(null);
                setDetailScrollToSection(undefined);
              }}
              isModal={true}
              scrollToSection={detailScrollToSection}
              onCreate={(newCharacter) => {
                const createdCharacter: Character = {
                  id: newCharacter.id,
                  name: newCharacter.name,
                  userNotes: newCharacter.userNotes,
                  isManual: newCharacter.isManual,
                  relevance: clampEntityRelevance(newCharacter.relevance),
                };
                setCharacters((prev) => [createdCharacter, ...prev]);
                setSelectedCharacterId(null);
                void dispatchMentionableEntitiesChangedForDocument(documentId, token ?? null, masterKey, projectId);
              }}
              onUpdated={(updatedCharacter) => {
                setCharacters((prev) =>
                  prev.map((character) =>
                    character.id === updatedCharacter.id
                      ? {
                          ...character,
                          name: updatedCharacter.name,
                          description: updatedCharacter.description,
                          userNotes: updatedCharacter.userNotes,
                          relevance: clampEntityRelevance(updatedCharacter.relevance),
                        }
                      : character
                  )
                );
              }}
              onDeleted={(deletedCharacterId) => {
                setCharacters((prev) => prev.filter((character) => character.id !== deletedCharacterId));
                setSelectedCharacterId(null);
              }}
            />
        </DialogContent>
      )}
    </Dialog>
  );

  if (isSidebar) {
    return (
      <>
        {content}
        {characterDetailModal}
      </>
    );
  }

  return (
    <>
      {content}
      {characterDetailModal}
    </>
  );
};

